import type { OnboardingStepKey } from './OnboardingPanel';

export const STARTED_STORAGE_PREFIX = 'bower_onboarding_v2_started:';
export const ORCID_STORAGE_PREFIX = 'bower_onboarding_v2_orcid:';
export const RESUME_STORAGE_PREFIX = 'bower_onboarding_v2_workspace:';
/**
 * The wizard draft — step, workspace name, and research answers. localStorage,
 * not sessionStorage: the point of it is surviving a CLOSED TAB, not just a
 * refresh. Cleared on completion.
 */
export const DRAFT_STORAGE_PREFIX = 'bower_onboarding_v2_draft:';

/**
 * Which onboarding a user gets is decided by their relationship to the
 * workspace, not by "did they sign up or accept an invite":
 *
 *   new_user       creates the workspace, so they answer FOR it — its name,
 *                  its field, its team size.
 *   invited_writer joins a workspace that already answered those, but can
 *                  write, so they do everything else.
 *   invited_guest  is read-only (GUEST live, VIEWER deprecated). Import and
 *                  mobile capture are closed to them app-wide
 *                  (`useIsReadOnly`) — both write — so offering those steps
 *                  would be dead ends. Bird chat is NOT one of these
 *                  anymore: `useCanUseBird` admits every resolved role,
 *                  guests included, read-only. Ask is still skipped, but
 *                  because it isn't in `INVITED_GUEST_STEP_KEYS` below, not
 *                  because Bird is closed to them.
 */
export type OnboardingCohort = 'new_user' | 'invited_writer' | 'invited_guest';

/** The complete flow — new users and invited writers alike. */
export const FULL_STEP_KEYS = [
  'you',
  'research',
  'knowledge',
  'ask',
  'mobile',
] as const satisfies readonly OnboardingStepKey[];

/** Read-only invitees stop at Join, where their access level is explained. */
export const INVITED_GUEST_STEP_KEYS = ['you', 'research', 'join'] as const;

export function onboardingStepKeys(
  cohort: OnboardingCohort,
): readonly (OnboardingStepKey | 'join')[] {
  return cohort === 'invited_guest' ? INVITED_GUEST_STEP_KEYS : FULL_STEP_KEYS;
}

/**
 * The last step reachable without a workspace. `createWorkspace` runs on
 * LEAVING research, so for a new user who hasn't got that far, every later step
 * would render against a null workspace id — an import with no parent, an Ask
 * with no scope. Invited cohorts always have a workspace, so this never binds
 * for them.
 */
const LAST_PRE_WORKSPACE_STEP = 'research';

interface ResolveInitialStepInput {
  readonly cohort: OnboardingCohort;
  /** `?step` from a magic link. Highest priority — the user just asked for it. */
  readonly urlStepKey: string | null;
  /** Where the local draft says they were. */
  readonly draftStepKey: string | null;
  /** Server-derived default when neither hint applies (a resuming owner). */
  readonly fallbackStepKey: string | null;
  /** Whether the onboarding workspace exists yet. */
  readonly workspaceExists: boolean;
}

/**
 * Which hint actually won — NOT which one was present.
 *
 * Reported as `ONBOARDING_RESUMED.source`, where the distinction is the whole
 * point: a fresh device with no draft wins on `server` while a `?step` may
 * still be absent, and a cohort-rejected `?step` falls through to `draft`.
 * Deriving the label from "was a param supplied?" mislabels both, which is
 * exactly the phone-handoff-vs-same-browser confusion the dimension exists to
 * prevent.
 */
export type OnboardingResumeSource = 'magic_link' | 'draft' | 'server';

export interface ResolvedInitialStep {
  readonly index: number;
  /** Null when nothing resolved and the wizard simply opens at step one. */
  readonly source: OnboardingResumeSource | null;
}

/**
 * Which step to open on. Every hint is exactly that — a HINT, clamped by what
 * the server says about this user.
 *
 * A candidate absent from this cohort's list falls THROUGH to the next
 * candidate rather than clamping into it. Clamping would be actively wrong: a
 * `?step=mobile` reaching a read-only guest (list `you → research → join`)
 * would land on `join`, the terminal step, silently skipping them to the end of
 * a flow they never walked.
 */
export function resolveInitialStep({
  cohort,
  urlStepKey,
  draftStepKey,
  fallbackStepKey,
  workspaceExists,
}: ResolveInitialStepInput): ResolvedInitialStep {
  const keys = onboardingStepKeys(cohort) as readonly string[];
  const ceiling = workspaceExists
    ? keys.length - 1
    : Math.max(0, keys.indexOf(LAST_PRE_WORKSPACE_STEP));

  const candidates = [
    { key: urlStepKey, source: 'magic_link' },
    { key: draftStepKey, source: 'draft' },
    { key: fallbackStepKey, source: 'server' },
  ] as const satisfies readonly { key: string | null; source: OnboardingResumeSource }[];

  for (const { key, source } of candidates) {
    if (!key) continue;
    const index = keys.indexOf(key);
    if (index < 0) continue;
    return { index: Math.min(index, ceiling), source };
  }
  return { index: 0, source: null };
}

export const STEP_LABELS: Record<string, string> = {
  you: 'You',
  research: 'Research',
  knowledge: 'Knowledge',
  ask: 'Ask Bower',
  mobile: 'Mobile',
  join: 'Join',
};

/**
 * Panel visibility is a property of the STEP, not of the cohort — an invited
 * writer sees the same Knowledge and Mobile steps and wants the same value
 * prop beside them. Ask owns the full width for the conversation; Join is a
 * terminal confirmation with nothing left to sell.
 */
export function onboardingContentLayout(currentKey: string) {
  const showPanel = currentKey !== 'ask' && currentKey !== 'join';
  let contentMaxWidth: string | undefined;
  if (!showPanel) contentMaxWidth = currentKey === 'ask' ? '860px' : '560px';
  return {
    showPanel,
    contentMaxWidth,
  };
}
